import React, { createContext, useContext, useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const CACHE_KEY = "hoffmann_content_cache";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

const ContentContext = createContext(null);
export const useContent = () => useContext(ContentContext) || { texts: {}, images: {}, colors: {}, loading: true };

export const ContentProvider = ({ children }) => {
  const [content, setContent] = useState(() => {
    try {
      const cached = sessionStorage.getItem(CACHE_KEY);
      if (cached) {
        const { data, ts } = JSON.parse(cached);
        if (Date.now() - ts < CACHE_TTL) return data;
      }
    } catch (e) { /* cached data parse failed, will fetch fresh */ }
    return null;
  });
  const [loading, setLoading] = useState(!content);

  useEffect(() => {
    if (content) { setLoading(false); }
    // Always fetch fresh in background
    axios.get(`${API}/content`)
      .then((res) => {
        setContent(res.data);
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ data: res.data, ts: Date.now() }));
      })
      .catch(() => { /* content fetch failed, using cache or defaults */ })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const texts = content?.texts || {};
  
  // Resolve image URLs: /uploads/... paths need backend URL prefix, fallback to defaults
  const images = React.useMemo(() => {
    const raw = content?.images || {};
    const defaults = {
      hero: "https://images.unsplash.com/photo-1696987007764-7f8b85dd3033?w=1400&q=75&fm=jpg",
      team: "/images/team.jpg",
      vehicles: "/images/vehicles.jpg",
      heizung: "https://images.unsplash.com/photo-1600210492493-0946911123ea?w=900&q=75&fm=jpg",
      heizung2: "https://images.unsplash.com/photo-1726090401458-7abb00f7450c?w=900&q=75&fm=jpg",
      heizung3: "https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=900&q=75&fm=jpg",
      bad: "https://images.unsplash.com/photo-1696987007764-7f8b85dd3033?w=900&q=75&fm=jpg",
      bad2: "https://images.unsplash.com/photo-1507652313519-d4e9174996dd?w=900&q=75&fm=jpg",
      bad3: "https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?w=900&q=75&fm=jpg",
      solar: "https://images.unsplash.com/photo-1558449028-b53a39d100fc?w=900&q=75&fm=jpg",
      solar2: "https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?w=900&q=75&fm=jpg",
      solar3: "https://images.unsplash.com/photo-1509391366360-2e959784a276?w=900&q=75&fm=jpg",
      smarthome: "https://images.unsplash.com/photo-1558002038-1055907df827?w=900&q=75&fm=jpg",
      smarthome2: "https://images.unsplash.com/photo-1513584684374-8bab748fbf90?w=900&q=75&fm=jpg",
      smarthome3: "https://images.pexels.com/photos/29091470/pexels-photo-29091470.jpeg?auto=compress&w=900",
      kontakt_hero: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=900&q=75&fm=jpg",
      karriere_hero: "/images/team.jpg",
      cta_bg: "https://images.pexels.com/photos/28456461/pexels-photo-28456461.jpeg?auto=compress&w=1200",
      technician: "https://images.pexels.com/photos/35290669/pexels-photo-35290669.jpeg?auto=compress&w=900",
      klima: "https://images.unsplash.com/photo-1631545806609-4146382b42c1?w=900&q=75&fm=jpg",
      klima2: "https://images.unsplash.com/photo-1585771724684-38269d6639fd?w=900&q=75&fm=jpg",
      klima3: "https://images.unsplash.com/photo-1581578731548-c64695cc6952?w=900&q=75&fm=jpg",
    };
    const merged = { ...defaults, ...raw };
    const resolved = {};
    for (const [key, val] of Object.entries(merged)) {
      if (val && val.startsWith("/uploads/")) {
        resolved[key] = `${BACKEND_URL}${val}`;
      } else {
        resolved[key] = val || defaults[key] || "";
      }
    }
    return resolved;
  }, [content?.images]);
  const colors = React.useMemo(() => content?.colors || {}, [content?.colors]);

  useEffect(() => {
    if (colors.primary) {
      const root = document.documentElement;
      root.style.setProperty("--dyn-primary", colors.primary);
      root.style.setProperty("--dyn-accent", colors.accent || "#ffef38");
    }
  }, [colors]);

  return (
    <ContentContext.Provider value={{ texts, images, colors, loading }}>
      {children}
    </ContentContext.Provider>
  );
};
